<template>
  <v-card class="py-4" outlined>
    <v-data-table
      class="elevation-0"
      :headers="headers"
      :items="items"
      :items-per-page.sync="localItemsPerPage"
      :page.sync="localPage"
      item-key="rfq_id"
      :single-expand="false"
      :expanded.sync="localExpanded"
      sort-by="timecreate"
      sort-desc
      show-expand
      :footer-props="{ 'items-per-page-options': [10, 20, 30, 50] }"
      :style="{ '--date-font-scale': settingsStore.getFontSize }"
    >
      <!-- Partner name -->
      <template #[`item.partner_name`]='{ item }'>
        <v-chip
          v-if="isBroker && item.partner"
          small
          class="partner-chip"
        >
          {{ item.partner.name || item.partner.id || '-' }}
        </v-chip>
        <span v-else>-</span>
      </template>

      <!-- Maturity date -->
      <template #[`item.rfq_sett_brl`]="{ item }">
        <span>{{ item.rfq_security === 'FXNDF' ? formatMaturityDate(item.rfq_sett_brl) : '-' }}</span>
      </template>

      <!-- Deal time -->
      <template #[`item.timecreate`]="{ item }">
        <span class="report-date-cell"> {{ formatTimeAndDate(item.timecreate) }}</span>
      </template>

      <!-- Amount -->
      <template #[`item.rfq_amount`]="{ item }">
        <v-chip
          small
          :text-color="item.rfq_side === 'BUY' ? 'alive' : 'orangeColor'"
          :color="item.rfq_side === 'BUY' ? 'aliveTransp' : 'yellowTransp'"
        >
          <span class="font-weight-bold">{{ formatPrice(item.rfq_amount) }}</span>
        </v-chip>
      </template>

      <!-- Side -->
      <template #[`item.rfq_side`]="{ item }">
        <span
          class="font-weight-bold"
          :class="{
            'alive--text':       item.rfq_side === 'BUY',
            'orangeColor--text': item.rfq_side === 'SELL',
          }"
        >
          {{ item.rfq_side }}
        </span>
      </template>

      <!-- Spot settlement -->
      <template #[`item.rfq_spotsett`]="{ item }">
        <span>{{ item.rfq_security !== 'FXNDF' ? item.rfq_spotsett?.toUpperCase() : '-' }}</span>
      </template>

      <!-- Client Rate -->
      <template #[`item.rfq_px`]="{ item }">
        <span :style="item.rfq_status !== 'DEAL' ? 'color: #9E9E9E;' : ''">
          {{ item.rfq_px != null ? Number(item.rfq_px).toFixed(4) : '-' }}
          <v-icon
            v-if="item.reconciliation_hash_valid === false"
            small
            color="warning"
            class="ml-1"
            :title="$t('reports.reconciliationHashInvalid')"
          >
            mdi-alert
          </v-icon>
        </span>
      </template>

      <!-- Volume BRL -->
      <template #[`item.volume_brl`]="{ item }">
        <v-chip
          v-if="item.volume_brl != null"
          small
          :text-color="item.rfq_status === 'DEAL' ? (item.rfq_side === 'BUY' ? 'alive' : 'orangeColor') : '#9E9E9E'"
          :color="item.rfq_status === 'DEAL' ? (item.rfq_side === 'BUY' ? 'aliveTransp' : 'yellowTransp') : '#2C2C2C'"
        >
          <span class="font-weight-bold">{{ formatVolumeBrl(item.volume_brl) }}</span>
        </v-chip>
        <span v-else>-</span>
      </template>

      <!-- Company name -->
      <template #[`item.company_name`]="{ item }">
        <span>{{ item.company && item.company.name ? item.company.name : '-' }}</span>
      </template>

      <!-- Status -->
      <template #[`item.rfq_status`]="{ item }">
        <span>{{ formatStatus(item.rfq_status) }}</span>
      </template>

      <!-- Row actions -->
      <template #[`item.actions`]="{ item }">
        <div class="d-flex flex-row flex-gap-2">
          <v-btn x-small color="primary" @click="$emit('export-single-pdf', item)">            <span>PDF</span>
          </v-btn>
          <v-btn x-small outlined color="primary" @click="$emit('export-single-csv', item)">            <span style="color:white">CSV</span>
          </v-btn>
        </div>
      </template>

      <!-- Expanded row -->
      <template v-slot:expanded-item="{ headers, item }">
        <reports-expanded-row
          :item="item"
          :headers="headers"
          :is-broker="isBroker"
          :is-partner="isPartner"
          :is-pro="isPro"
          side-field="rfq_side"
          @reconcile="handleReconcileDealData"
        >
          <template #ids="{ item: row }">
            <!-- RFQ ID -->
            <div class="d-flex flex-wrap align-center mb-2">
              <copyable-id :id="row.rfq_id" type="RFQ" :number-of-charts="50"/>
            </div>

            <!-- Operation Name -->
            <div class="d-flex flex-wrap align-center mb-2">
              <span class="input-title-text font-weight-bold mr-2">{{ $t('quoteInfo.opName') }}:</span>
              <span>{{ row.operation_alias || '-' }}</span>
            </div>

            <!-- Operation Type -->
            <div class="d-flex flex-wrap align-center mb-2">
              <span class="input-title-text font-weight-bold mr-2">{{ $t('broker.labels.transactionPurpose') }}:</span>
              <span>{{ formatOperationTypeTitle(row.operation_type) }}</span>
            </div>
          </template>
        </reports-expanded-row>
      </template>

    </v-data-table>

      <the-rfq-window
      v-model="reconcileModal"
      mode="reconciliation"
      :rfq-to-manual-price="item"
      @reconcileSubmit="handleReconcileSubmit"
    />
  </v-card>
</template>

<script>
import * as utils from '@/utils.js'
import ReportsExpandedRow from '@/components/reports/ReportsExpandedRow.vue'
import CopyableId         from '@/components/common/CopyableId.vue'
import TheRfqWindow from '../modal/TheRfqWindow.vue';

import {
  createReportDateComparator,
  createReportNumberComparator,
  createReportTextComparator,
  formatReportMaturityDate,
  formatReportTimelineDate,
  translateStatus
} from '@/components/reports/reportHelpers.js'
import { useExecutionStore, useSnackbarStore, useSettingsStore } from '@/store/index.js';

export default {
  name: 'ReportsRfqTable',
  components: { ReportsExpandedRow, CopyableId, TheRfqWindow },
  
  props: {
    items:     { type: Array,   required: true },
    expanded:  { type: Array,   default: () => [] },
    page:      { type: Number,  default: 1 },
    itemsPerPage: { type: Number,  default: 10 },
    isBroker:  { type: Boolean, default: false },
    isPartner: { type: Boolean, default: false },
    isPro:     { type: Boolean, default: false },
    locale:    { type: String,  default: 'en' },
    now:       { type: Number,  default: () => Date.now() },
  },
  data(){
    return {
      reconcileModal: false,
      item: {},
      settingsStore: useSettingsStore(),
    }
  },
  emits: ['update:expanded', 'update:page', 'update:items-per-page', 'export-single-pdf', 'export-single-csv', 'reconciled'],
  computed: {
    localExpanded: {
      get () { return this.expanded },
      set (val) { this.$emit('update:expanded', val) }
    },
    localPage: {
      get () { return this.page },
      set (val) { this.$emit('update:page', val) }
    },
    localItemsPerPage: {
      get () { return this.itemsPerPage },
      set (val) { this.$emit('update:items-per-page', val) }
    },
    headers () {
      const compareText = createReportTextComparator(this.locale)
      const compareNumber = createReportNumberComparator()
      const compareDate = createReportDateComparator()

      const base = [
        { text: this.$t('quoteInfo.clientName'), value: 'company_name', sortable: true, sort: compareText },
        { text: this.$t('quoteInfo.dealTime'),    value: 'timecreate', sortable: true, sort: compareDate },
        { text: this.$t('quoteInfo.ccy'),         value: 'rfq_ccy',       sortable: true, sort: compareText },
        { text: this.$t('quoteInfo.amountCcy'),   value: 'rfq_amount',    sortable: true, sort: compareNumber },
        { text: this.isPro ? this.$t('quoteInfo.rate') : this.$t('quoteInfo.clientRate'),  value: 'rfq_px', sortable: true, sort: compareNumber },
        { text: this.$t('quoteInfo.volumeCcyBrl'), value: 'volume_brl',   sortable: true, sort: compareNumber },
        { text: this.$t('quoteInfo.side'),        value: 'rfq_side',      sortable: true, sort: compareText },
        { text: this.$t('quoteInfo.settlement'),  value: 'rfq_spotsett',  sortable: true, sort: compareText },
        { text: this.$t('quoteInfo.status'),      value: 'rfq_status',    sortable: true, sort: compareText },
        { text: '',                                value: 'actions',       sortable: false },
      ]
      if (this.isBroker) {
        base.unshift({ text: this.$t('reports.partnerName'), value: 'partner_name', sortable: false })
      }
      return base
    },
  },
  methods: {
    formatTimeAndDate (ts) {
      return formatReportTimelineDate(ts)
    },
    formatPrice (price) {
      return utils.formatPrice(price)
    },
    formatVolumeBrl (volume) {
      const n = Number(volume)
      if (!Number.isFinite(n)) return '-'
      return `R$ ${utils.formatBRLPretty(n)}`
    },
    formatMaturityDate (date) {
      return formatReportMaturityDate(date)
    },
    formatStatus(status) {
      return translateStatus(status)
    },
    formatOperationTypeTitle (type) {
      const operationTypes = this.$t('broker.operationTypes')
      return String(operationTypes?.[type] || type || '-')
    },
    handleReconcileDealData(item)
    {
      if (!item) {
        this.item = { message: null }
        return
      }
      // item.quote_list is the array of bank quotes for this RFQ (set by
      // ReportsExpandedRow); the dealt/accepted quote holds the actual bank
      // that was traded with, used below for bank_id/bank_name.
      const quoteList = Array.isArray(item.quote_list) ? item.quote_list : []
      const dealtQuote = quoteList.find((q) => q.deal) || quoteList[0] || {}
      this.item = {
        message: {
          ...item,
          rfq_id: item.rfq_id,
          bank_id: item.bank_id ?? dealtQuote.bank_id ?? null,
          bank_name: dealtQuote.bank_label || item.bank_name || '-',
          rfq_orderqty: item.amount ?? item.rfq_orderqty ?? item.rfq_amount,
          rfq_side: item.side ?? item.rfq_side,
          rfq_orderqty_ccy: item.rfq_orderqty_ccy ?? item.pair ?? item.rfq_ccy,
          rfq_px: item.rfq_px, // current client rate
          timecreate: item.timecreate ?? 0,
          rfq_security: item.rfq_security || 'FXSPOT',
          partmaster_id: item.partmaster_id ?? 0,
          broker_name: item.broker_name || '',
          company_cnpj: item.company?.cnpj || '',
        }
      }
      this.reconcileModal = true;
    },

    async handleReconcileSubmit(data, completion = {}){
      const result = await utils.submitReconcile(useExecutionStore(), data)
      if (result.ok) {
        useSnackbarStore().open(this.$t('broker.snackBar.reconcileSuccess'))
      } else {
        useSnackbarStore().open(this.$t(`broker.snackBar.${result.key}`), 'error')
      }
    }
  }
}
</script>

<style scoped>
.report-date-cell {
  display: inline-block;
  font-size: calc(clamp(0.6rem, 1.4vw, 0.8rem) * var(--date-font-scale, 1));
  line-height: 1.25;
  white-space: normal;
}
</style>
